import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation, useMatch } from 'react-router-dom';
import { useAuth } from './store';
import { useMe } from './useMe';
import { homePathForRoles } from './role-home';
import { loginPath, rememberedLoginKey } from './login-link';

/**
 * Guards for authenticated / guest-only routes.
 *
 * Auth state has two sources of truth that can disagree after a session
 * expires: the persisted zustand snapshot in localStorage, and the live
 * result of `GET /api/v1/me`. `/me` is authoritative — if it says 401 we
 * must not keep rendering a "signed in" shell just because localStorage
 * still remembers a user. Failing to reconcile the two caused a redirect
 * loop between /dashboard (AuthGuard trusts stored user) and /login
 * (GuestGuard also trusts stored user and bounces back).
 */

export function AuthGuard({ children }: { children: ReactNode }) {
  const location = useLocation();
  const storedUser = useAuth((s) => s.user);
  const clear = useAuth((s) => s.clear);
  const { data, isLoading, isError } = useMe();

  // If the persisted session says "signed in" but /me disagrees, drop the
  // stale snapshot so GuestGuard doesn't send us straight back here.
  useEffect(() => {
    if (storedUser && isError) clear();
  }, [storedUser, isError, clear]);

  // First paint on a cold cache: no persisted user AND /me is still in flight.
  if (!storedUser && isLoading) {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }

  // Either no persisted user and /me came back empty/errored, or we just
  // cleared the persisted user because /me errored — go to /login.
  if ((!storedUser && (isError || !data)) || isError) {
    return <Navigate to={loginPath()} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

export function GuestGuard({ children }: { children: ReactNode }) {
  const user = useAuth((s) => s.user);
  const roles = useAuth((s) => s.roles);
  // Matched explicitly rather than via useParams: this guard is the parent
  // layout route, and the key belongs to its child.
  const loginKey = useMatch('/login/:loginKey')?.params.loginKey;

  // Opening a DIFFERENT company's sign-in link while signed in is a deliberate
  // act — the link names the workspace — so show its sign-in page; signing in
  // there replaces the current session. Bouncing to the dashboard instead
  // would silently keep the person in the company they were trying to leave.
  const openingAnotherLink = !!loginKey && loginKey !== rememberedLoginKey();

  // Otherwise, already signed in — bounce to the role-appropriate landing
  // page (salesperson → pipeline, employee → self-service, everyone else →
  // dashboard). This keeps a demo Employee who reloads /login from landing
  // back on /dashboard and staring at KPIs they can't see.
  if (user && !openingAnotherLink) return <Navigate to={homePathForRoles(roles)} replace />;
  return <>{children}</>;
}
